import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency, formatDate } from '../../utils/formatters.js';

const STATUS_VARIANT = { OPEN: 'warning', IN_PROGRESS: 'info', COMPLETED: 'success', CANCELLED: 'neutral' };
const PRIO_VARIANT = { LOW: 'neutral', MEDIUM: 'info', HIGH: 'warning', URGENT: 'danger' };

export function MaintenanceOrderDetailModal({ orderId, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [costs, setCosts] = useState({ materialCost: '', laborCost: '' });
  const [error, setError] = useState(null);

  const detail = useQuery({ queryKey: ['finance', 'maintenance-order', orderId], queryFn: () => financeApi.getMaintenanceOrder(orderId).then((r) => r.items), enabled: Boolean(orderId) });
  const order = detail.data;

  useEffect(() => { if (order) setCosts({ materialCost: String(order.materialCost ?? ''), laborCost: String(order.laborCost ?? '') }); }, [order?.id]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['finance', 'maintenance-orders'] });
    queryClient.invalidateQueries({ queryKey: ['finance', 'maintenance-order', orderId] });
    queryClient.invalidateQueries({ queryKey: ['finance', 'equipment'] });
  };
  const run = (fn) => ({ mutationFn: fn, onSuccess: () => { setError(null); invalidate(); }, onError: (e) => setError(e?.message || t('common.loadError')) });
  const start = useMutation(run(() => financeApi.startMaintenanceOrder(orderId)));
  const complete = useMutation(run(() => financeApi.completeMaintenanceOrder(orderId)));
  const cancel = useMutation(run(() => financeApi.cancelMaintenanceOrder(orderId)));
  const saveCosts = useMutation(run(() => financeApi.setMaintenanceCosts(orderId, { materialCost: Number(costs.materialCost) || 0, laborCost: Number(costs.laborCost) || 0 })));
  const busy = start.isPending || complete.isPending || cancel.isPending;
  const editable = order && (order.status === 'OPEN' || order.status === 'IN_PROGRESS');

  return (
    <Modal
      open={Boolean(orderId)}
      onClose={onClose}
      title={order ? `${order.orderNumber} · ${order.title}` : t('pm.detail.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>
          {order?.status === 'OPEN' && <Button variant="primary" disabled={busy} onClick={() => start.mutate()}>{t('pm.action.start')}</Button>}
          {order?.status === 'IN_PROGRESS' && <Button variant="primary" disabled={busy} onClick={() => complete.mutate()}>{t('pm.action.complete')}</Button>}
          {editable && <Button variant="danger" disabled={busy} onClick={() => cancel.mutate()}>{t('pm.action.cancel')}</Button>}
        </>
      }
    >
      {order && (
        <div className="form-stack">
          <p className="muted text-sm" style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            <Badge variant={STATUS_VARIANT[order.status]}>{t(`pm.status.${order.status}`)}</Badge>
            <Badge variant={PRIO_VARIANT[order.priority]}>{t(`pm.priority.${order.priority}`)}</Badge>
            {order.equipmentNumber} · {order.equipmentName}
            {order.plannedDate ? ` · ${t('pm.field.plannedDate')}: ${formatDate(order.plannedDate)}` : ''}
          </p>
          {order.description && <p>{order.description}</p>}

          <div className="settings-divider">{t('pm.costs')}</div>
          <div className="field-row" style={{ alignItems: 'flex-end' }}>
            <label className="field"><span className="field-label">{t('pm.materialCost')}</span>
              <input className="input num" type="number" step="0.01" min="0" value={costs.materialCost} disabled={!editable} onChange={(e) => setCosts((c) => ({ ...c, materialCost: e.target.value }))} />
            </label>
            <label className="field"><span className="field-label">{t('pm.laborCost')}</span>
              <input className="input num" type="number" step="0.01" min="0" value={costs.laborCost} disabled={!editable} onChange={(e) => setCosts((c) => ({ ...c, laborCost: e.target.value }))} />
            </label>
            {editable && <Button variant="secondary" disabled={saveCosts.isPending} onClick={() => saveCosts.mutate()}>{t('common.save')}</Button>}
          </div>
          <p className="text-sm"><strong>{t('pm.col.cost')}: {formatCurrency(order.totalCost)}</strong></p>
          {error && <div className="alert danger">{error}</div>}
        </div>
      )}
    </Modal>
  );
}
